<template>
  <div class="flex flex-col gap-4">
    <span v-if="errorMessage" class="px-2 py-1 text-red-600 bg-red-100 rounded">
      {{ errorMessage }}
    </span>
    <h4 class="font-semibold">{{ $t("Payment Method") }}</h4>
    <div class="flex flex-wrap gap-2">
      <label
        class="cursor-pointer"
        v-for="gateway in gateways"
        :key="gateway.id"
        :for="gateway.id"
      >
        <input
          class="hidden"
          v-model="form.gateway"
          type="radio"
          name="gateway"
          :value="gateway.id"
          :id="gateway.id"
        />
        <span
          :class="{
            'border-red-500': form.gateway == gateway.id,
          }"
          class="flex items-center gap-3 px-4 py-2 font-semibold border divide-x-2 rounded-full"
        >
          <img :src="gateway.image" class="h-6" alt="" />
          <span class="pl-4">{{ gateway.name }}</span>
        </span>
      </label>
    </div>

    <div class="flex flex-col gap-4">
      <h4 class="font-semibold">{{ $t("Payment Data") }}</h4>

      <div class="p-4 rounded bg-zinc-100 dark:bg-zinc-900">
        <p>{{ currentGateway?.instruction }}</p>
      </div>
      <div v-if="currentGateway?.inputs.length">
        <div
          v-for="input in currentGateway.inputs"
          :key="input.name"
          class="mb-4"
        >
          <label class="block mb-2 text-sm font-medium">
            {{ $t(input.name) }}*
          </label>
          <input
            v-model="form[input.name]"
            :type="input.type || 'text'"
            :placeholder="input.placeholder"
            :maxlength="input.validation?.maxLength"
            :class="[
              'w-full p-2 border rounded dark:bg-zinc-900 dark:text-white dark:border-gray-700',
              errors[input.name] ? 'border-red-500' : 'border-gray-300',
            ]"
            @blur="validateField(input)"
          />
          <span v-if="errors[input.name]" class="text-sm text-red-600">
            {{ errors[input.name] }}
          </span>
        </div>
      </div>

      <div class="form-group">
        <!-- <label class="block mb-2 text-sm font-medium"
          >{{ $t("Amount") }}*</label
        > -->
        <InputMoney
          v-model="form.amount"
          id="amount"
          :placeholder="$t('Amount')"
          :label="$t('Amount') + '*'"
          :error="errors.amount"
          inputClass="dark:bg-zinc-900 dark:text-white dark:border-gray-700"
          @blur="validateAmount"
        />
        
      </div>

      <div class="flex justify-end mt-4">
        <slot :submit="setUserBalance" :processing="loading">
          <button
            type="submit"
            @click="handleSubmit"
            class="w-full px-4 py-2 font-semibold text-white bg-blue-600 rounded hover:bg-blue-600"
            v-if="!loading"
          >
            {{ $t("Recharge") }}
          </button>
          <button
            type="button"
            class="w-full px-4 py-2 font-semibold text-white bg-blue-600 rounded opacity-50 cursor-not-allowed"
            disabled
            v-else
          >
            {{ $t("Processing") }}...
          </button>
        </slot>
      </div>
    </div>

    <!-- Processing Modal with Countdown -->
    <div
      v-if="showProcessingModal"
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
    >
      <div
        class="relative w-full max-w-md mx-4 overflow-hidden bg-white dark:bg-zinc-800 rounded-2xl shadow-2xl"
      >
        <!-- Progress bar at top -->
        <div class="h-1.5 bg-gray-200 dark:bg-zinc-700">
          <div
            class="h-full transition-all duration-1000 ease-linear bg-gradient-to-r from-blue-500 to-purple-600"
            :style="{ width: countdownPercent + '%' }"
          ></div>
        </div>

        <div class="p-8 text-center">
          <!-- M-Pesa Logo -->
          <div class="flex justify-center mb-6">
            <img
              src="/assets/img/mpesa.svg"
              alt="M-Pesa"
              class="h-16 w-auto drop-shadow-lg"
            />
          </div>

          <!-- Spinner -->
          <div class="flex justify-center mb-6">
            <div
              class="w-16 h-16 border-4 border-blue-200 dark:border-zinc-600 border-t-blue-600 dark:border-t-blue-500 rounded-full animate-spin"
            ></div>
          </div>

          <!-- Title -->
          <h3 class="mb-2 text-xl font-bold text-gray-800 dark:text-white">
            {{ $t("ProcessingModal.title") }}
          </h3>

          <!-- Message -->
          <p class="mb-4 text-sm text-gray-600 dark:text-zinc-300">
            {{ $t("ProcessingModal.message") }}
          </p>

          <!-- Countdown -->
          <div
            class="flex items-center justify-center gap-2 py-3 mb-2 text-2xl font-bold text-blue-600"
          >
            <span class="text-3xl">{{ formattedMinutes }}</span>
            <span>:</span>
            <span class="text-3xl">{{ formattedSeconds }}</span>
          </div>

          <p class="text-xs text-gray-500 dark:text-zinc-400">
            {{ $t("ProcessingModal.seconds", { seconds: countdown }) }}
          </p>
        </div>

        <!-- Cancel button -->
        <div class="flex justify-center pb-4">
          <button
            @click="cancelProcessing"
            class="px-6 py-2 text-sm font-medium text-gray-600 dark:text-zinc-300 hover:text-gray-800 dark:hover:text-white transition-colors"
          >
            {{ $t("ProcessingModal.cancel") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import AgentStoreModule from "@/pages/seller/agents/AgentStoreModule";
import toastr from "toastr";
import { ref, computed, defineProps, defineEmits, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import InputMoney from "./InputMoney.vue";
const { t } = useI18n();
const loading = ref(false);
const errorMessage = ref("");
const errors = ref({});

// Processing modal state
const showProcessingModal = ref(false);
const countdown = ref(60);
const countdownInterval = ref(null);

const emit = defineEmits(["pay"]);
const props = defineProps({
  gateway: {
    type: String,
    default: "mpesa",
  },
});

const form = ref({
  gateway: props.gateway,
  amount: 1000,
  phone: "",
});

const gateways = ref([
  {
    id: "mpesa",
    image: "/assets/img/mpesa.svg",
    name: "MPesa",
    instruction: t("mpesa_gateway_instructions"),
    inputs: [
      {
        name: "phone",
        placeholder: "84/85xxxxxxx",
        validation: {
          required: true,
          pattern: /^8[45][0-9]{7}$/,
          minLength: 9,
          maxLength: 9,
        },
      },
    ],
  },
]);

const currentGateway = computed(() => {
  return gateways.value.find(({ id }) => id == form.value.gateway);
});

// Countdown computed values
const countdownPercent = computed(() => {
  return ((60 - countdown.value) / 60) * 100;
});

const formattedMinutes = computed(() => {
  return String(Math.floor(countdown.value / 60)).padStart(2, "0");
});

const formattedSeconds = computed(() => {
  return String(countdown.value % 60).padStart(2, "0");
});

function validateField(input) {
  const value = form.value[input.name];
  const validation = input.validation;

  if (validation?.required && !value) {
    errors.value[input.name] = `${t(input.name)} é obrigatório.`;
    return;
  }

  if (validation?.pattern && !validation.pattern.test(value)) {
    errors.value[input.name] = `${t(input.name)} inválido.`;
    return;
  }

  if (validation?.minLength && value.length < validation.minLength) {
    errors.value[input.name] = `${t(input.name)} deve ter pelo menos ${
      validation.minLength
    } caracteres.`;
    return;
  }

  if (validation?.maxLength && value.length > validation.maxLength) {
    errors.value[input.name] = `${t(input.name)} não pode exceder ${
      validation.maxLength
    } caracteres.`;
    return;
  }

  errors.value[input.name] = "";
}

function validateAmount() {
  if (!form.value.amount || form.value.amount <= 0) {
    errors.value.amount = "O valor deve ser maior que zero.";
  } else {
    errors.value.amount = "";
  }
}

function startCountdown() {
  countdown.value = 60;
  countdownInterval.value = setInterval(() => {
    countdown.value--;
    if (countdown.value <= 0) {
      clearInterval(countdownInterval.value);
      countdownInterval.value = null;
      // Keep modal open until API resolves, but stop the timer
    }
  }, 1000);
}

function stopCountdown() {
  if (countdownInterval.value) {
    clearInterval(countdownInterval.value);
    countdownInterval.value = null;
  }
}

function cancelProcessing() {
  stopCountdown();
  showProcessingModal.value = false;
  loading.value = false;
}

async function handleSubmit() {
  errors.value = {};

  // console.log("====================================");
  // console.log(Object.keys(currentGateway.value?.inputs || []));
  // console.log("====================================");
  Object.keys(currentGateway.value?.inputs || []).forEach((index) => {
    validateField(currentGateway.value.inputs[index]);
  });
  validateAmount();

  if (Object.values(errors.value).some((error) => error)) {
    toastr.error("Verifique os campos obrigatórios.");
    return;
  }

  // Show processing modal with countdown
  showProcessingModal.value = true;
  startCountdown();

  await setUserBalance();
}

async function setUserBalance() {
  loading.value = true;
  errorMessage.value = "";
  try {
    const { data } = await AgentStoreModule.actions.setUserBalance(form.value);
    toastr.success("Pagamento processado com sucesso");
    emit("pay", data);
  } catch (error) {
    errorMessage.value =
      error.response?.data?.message || error.message || "Erro inesperado.";
    toastr.error(errorMessage.value);
  } finally {
    loading.value = false;
    stopCountdown();
    showProcessingModal.value = false;
  }
}

// Cleanup interval on unmount
onUnmounted(() => {
  stopCountdown();
});
</script>
